import React, { useEffect, useState } from 'react';
import { Link, useSearchParams, useParams } from 'react-router-dom';
import { FaArrowRight } from 'react-icons/fa6';
import { fetchJSON } from '../lib/api';
import { useAuth } from '../lib/auth';

const FIRST_LESSON = '/courses/ai-for-parents/module-1/intro';

export default function CourseWelcome() {
  useParams();
  const { user, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');

  const [status, setStatus] = useState('loading');
  const [purchaser, setPurchaser] = useState({ name: null, email: null });

  useEffect(() => {
    document.title = 'Welcome — Learning Outcomes Academy';
    return () => { document.title = 'Learning Outcomes Academy'; };
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setStatus('success');
      return;
    }
    fetchJSON(`/api/checkout/status?session_id=${encodeURIComponent(sessionId)}`)
      .then((data) => {
        setPurchaser({ name: data.name, email: data.email });
        setStatus('success');
      })
      .catch(() => setStatus('success'));
  }, [sessionId]);

  // Derive first name: prefer billing name from Stripe, fall back to auth user name
  const displayName = purchaser.name || (user && user.name) || null;
  const firstName = displayName ? displayName.split(' ')[0] : null;

  // Email to show in the "check your inbox" message
  const email = purchaser.email || (user && user.email) || null;

  if (status === 'loading' || authLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="animate-pulse text-slate2">Confirming your purchase…</div>
      </div>
    );
  }

  return (
    <div className="w-full bg-bone min-h-[70vh]">
      <div className="container-page py-20 max-w-2xl">

        <div className="text-[11px] tracking-[0.28em] uppercase text-brand-aubergine mb-6">
          Generative AI for Parents
        </div>

        <h1 className="font-display text-4xl sm:text-5xl leading-tight tracking-tighter2 text-ink mb-6">
          {firstName ? `Welcome, ${firstName}.` : 'Welcome!'}
          <span className="block italic text-brand-deep mt-1">You're enrolled.</span>
        </h1>

        {user ? (
          /* ── Signed-in path: go straight to the course ── */
          <>
            <p className="text-lg leading-relaxed text-slate2 mb-10">
              You're signed in as <strong className="text-ink">{user.email}</strong>.
              You're ready to start — click below to begin your first lesson.
            </p>
            <Link
              to={FIRST_LESSON}
              className="inline-flex items-center gap-2 bg-ink text-bone font-medium px-6 py-3.5 rounded-full hover:bg-brand-deep transition-colors"
            >
              Start your course <FaArrowRight className="text-xs" />
            </Link>
          </>
        ) : (
          /* ── Not signed in: prompt them to check email ── */
          <>
            <p className="text-lg leading-relaxed text-slate2 mb-4">
              We've sent a sign-in link to{' '}
              {email
                ? <strong className="text-ink">{email}</strong>
                : 'your inbox'
              }.{' '}
              Click it to sign in and start your course — check your spam folder if
              you don't see it within a minute or two.
            </p>

            <p className="text-sm text-slate2/70 mb-10">
              Didn't get it?{' '}
              <Link to="/login" className="underline underline-offset-4 hover:text-ink transition-colors">
                Request a new sign-in link
              </Link>.
            </p>
          </>
        )}

        <div className="mt-16 pt-10 border-t border-ink/10">
          <p className="text-sm text-slate2 leading-relaxed">
            <strong className="text-ink">What to expect:</strong> work through each lesson
            at your own pace. Answer the check-your-understanding question at the end of each
            lesson, then pass the final assessment to earn your certificate of completion.
            You can find your course any time in{' '}
            <Link to="/my-courses" className="underline underline-offset-4 hover:text-ink transition-colors">
              My Courses
            </Link>.
          </p>
        </div>

      </div>
    </div>
  );
}
